import { useState, useCallback, useRef, type ReactNode } from "react";

interface ResizablePanelsProps {
  left: ReactNode;
  right: ReactNode;
  defaultLeftPercent?: number;
  minLeftPercent?: number;
  maxLeftPercent?: number;
  className?: string;
}

export function ResizablePanels({
  left,
  right,
  defaultLeftPercent = 35,
  minLeftPercent = 20,
  maxLeftPercent = 60,
  className = "",
}: ResizablePanelsProps) {
  const [leftPercent, setLeftPercent] = useState(defaultLeftPercent);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragging.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPercent(Math.min(maxLeftPercent, Math.max(minLeftPercent, pct)));
    };

    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [minLeftPercent, maxLeftPercent]);

  return (
    <div
      ref={containerRef}
      className={`flex w-full ${className}`}
      style={{ minHeight: 0 }}
    >
      <div style={{ width: `${leftPercent}%` }} className="shrink-0 min-w-0">
        {left}
      </div>
      <div
        onMouseDown={onMouseDown}
        className="w-2 shrink-0 cursor-col-resize flex items-center justify-center group"
      >
        <div className="w-0.5 h-full rounded-full bg-transparent group-hover:bg-indigo-400 dark:group-hover:bg-indigo-400 transition-colors" />
      </div>
      <div className="flex-1 min-w-0">
        {right}
      </div>
    </div>
  );
}
